import {useCallback, useMemo} from 'react';
import {styled} from 'styled-components';

import {SvgIcon} from '@shared-web/components/core/svg_icon';
import {crossIcon} from '@shared-web/components/icons/cross_icon';
import {Custom} from '@shared-web/lib/react';
import {useStringArrayQueryString} from '@shared-web/lib/use_query_string';

import {CHAMPIONS} from '@src/lib/champions';
import {Team, TEAM_METADATA} from '@src/lib/teams';

interface ChampionCardProps {
  team: Team;
  champion?: (typeof CHAMPIONS)[0];
  showHighlight?: boolean;
  showRemove?: boolean;
}

export const ChampionCard: Custom<ChampionCardProps, 'div'> = props => {
  const {team, champion, showHighlight, showRemove, ...rest} = props;
  const [teamChampions, setTeamChampions] = useStringArrayQueryString(team, []);
  const {color} = TEAM_METADATA[team];

  const handleClick = useCallback(() => {
    const label = champion?.label ?? '';
    const isSelected = teamChampions.includes(label);
    setTeamChampions(curr => (isSelected ? curr.filter(l => l !== label) : [...curr, label]));
  }, [champion?.label, setTeamChampions, teamChampions]);

  const isActive = useMemo(
    () => teamChampions.includes(champion?.label ?? ''),
    [champion, teamChampions]
  );

  if (!champion) {
    return <Wrapper />;
  }

  return (
    <Wrapper
      key={champion.label}
      style={{backgroundImage: `url(${champion.image})`}}
      $highlighted={showHighlight && isActive ? color : undefined}
      {...rest}
    >
      <Label>{champion.label}</Label>
      {isActive && showRemove ? (
        <UnselectIcon onClick={handleClick}>
          <SvgIcon icon={crossIcon} color={'#ffffffaa'} />
        </UnselectIcon>
      ) : (
        <></>
      )}
    </Wrapper>
  );
};

ChampionCard.displayName = 'ChampionCard';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const HEIGHT_WIDTH_RATIO_PERCENT = (100 * 560) / 496;

const Wrapper = styled.div<{$highlighted?: string}>`
  width: 100%;
  height: 0;
  padding-top: ${HEIGHT_WIDTH_RATIO_PERCENT}%;
  background-size: cover;
  background-position: center center;
  position: relative;
  border-radius: 8px;
  ${p =>
    p.$highlighted !== undefined
      ? `
    box-shadow: 0 0 20px 5px ${p.$highlighted};
    z-index: 100;
    `
      : false}
`;
const Label = styled.div`
  font-size: 1.2vw;
  padding: 5px 0;
  font-weight: 500;
  letter-spacing: 1px;
  color: #fff;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  background-color: #000000b8;
`;

const UnselectIcon = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 100%;
  background-color: #ff000066;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 4px;
  right: 4px;
`;
